/**
 * Sales Portal API (client-safe)
 *
 * No .server suffix so client hooks can import without triggering
 * TanStack Start import-protection. Handlers run only on the server.
 *
 * Commission/transaction logic server-only; JWT + role check for sales access.
 * Tables: sales_teams, sales_team_members, sales_transactions, profiles, user_roles, commission_payouts.
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const jwtSchema = z
  .string()
  .min(1)
  .regex(/^[\w-]+\.[\w-]+\.[\w-]+$/, "Invalid JWT format");

/** Allowed tables for Sales Portal (whitelist). */
const SALES_PORTAL_TABLES = [
  "sales_teams",
  "sales_team_members",
  "sales_transactions",
  "profiles",
  "user_roles",
  "commission_payouts",
  "partners",
] as const;
type SalesPortalTable = (typeof SALES_PORTAL_TABLES)[number];

/** Roles that can access Sales Portal (commissions, analytics). */
const SALES_ROLES = ["admin", "manager", "sales_rep", "architect"] as const;

async function createAdminClient() {
  const { createClient } = await import("@supabase/supabase-js");
  const url = process.env.SUPABASE_URL ?? process.env.VITE_SUPABASE_URL;
  const serviceKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
  if (!url || !serviceKey) {
    return {
      client: null as null,
      error:
        "Missing SUPABASE_URL or SUPABASE_SERVICE_ROLE_KEY on server. Set these as environment variables.",
    };
  }
  const client = createClient(url, serviceKey, {
    auth: { persistSession: false, autoRefreshToken: false },
  });
  return { client, error: null as null };
}

function assertTable(table: string): table is SalesPortalTable {
  return (SALES_PORTAL_TABLES as readonly string[]).includes(table);
}

async function ensureSalesAccess(
  client: Awaited<ReturnType<typeof createAdminClient>>["client"],
  userId: string,
  accessToken: string
): Promise<{ error: string } | null> {
  if (!client) return { error: "Server not configured" };
  const {
    data: { user },
    error: authError,
  } = await client.auth.getUser(accessToken);
  if (authError || !user) return { error: "Unauthorized" };
  if (user.id !== userId) return { error: "Forbidden" };

  const { data: profile } = await client
    .from("profiles")
    .select("role")
    .eq("id", userId)
    .single();
  const role = (profile?.role as string | null) ?? "";
  const { data: roleRows } = await client
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  const roles = roleRows?.map((r) => r.role as string) ?? [];
  const hasSalesRole =
    (SALES_ROLES as readonly string[]).includes(role) ||
    roles.some((r) => (SALES_ROLES as readonly string[]).includes(r));
  if (!hasSalesRole) return { error: "Insufficient permissions for Sales Portal" };
  return null;
}

/** Get sales teams the user can access (manager or member; admin sees all). */
export const getSalesTeams = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      userId: z.string().uuid(),
      accessToken: jwtSchema,
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError };
    const access = await ensureSalesAccess(client, data.userId, data.accessToken);
    if (access) return { data: null, error: access.error };

    const table: SalesPortalTable = "sales_team_members";
    if (!assertTable(table)) return { data: null, error: "Invalid table" };
    const { data: memberTeams } = await client
      .from("sales_team_members")
      .select("team_id")
      .eq("user_id", data.userId)
      .eq("is_active", true);
    const memberTeamIds = memberTeams?.map((m) => m.team_id) ?? [];

    const teamsTable: SalesPortalTable = "sales_teams";
    if (!assertTable(teamsTable)) return { data: null, error: "Invalid table" };
    let teamsQuery = client.from("sales_teams").select("id, name, description, manager_id, commission_base_rate, is_active, settings, created_at, updated_at").eq("is_active", true);
    if (memberTeamIds.length > 0) {
      teamsQuery = teamsQuery.or(
        `manager_id.eq.${data.userId},id.in.(${memberTeamIds.join(",")})`
      );
    } else {
      teamsQuery = teamsQuery.eq("manager_id", data.userId);
    }
    const { data: teams, error } = await teamsQuery.order("name");
    if (error) return { data: null, error: error.message };
    return { data: teams ?? [], error: null };
  });

/** Get sales dashboard analytics for the user's teams. */
export const getSalesAnalytics = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      userId: z.string().uuid(),
      accessToken: jwtSchema,
      teamId: z.string().uuid().optional(),
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError };
    const access = await ensureSalesAccess(client, data.userId, data.accessToken);
    if (access) return { data: null, error: access.error };

    const memberTable: SalesPortalTable = "sales_team_members";
    if (!assertTable(memberTable)) return { data: null, error: "Invalid table" };
    const { data: memberRows } = await client
      .from("sales_team_members")
      .select("team_id")
      .eq("user_id", data.userId)
      .eq("is_active", true);
    const memberTeamIds = memberRows?.map((m) => m.team_id) ?? [];

    const teamsTable: SalesPortalTable = "sales_teams";
    if (!assertTable(teamsTable)) return { data: null, error: "Invalid table" };
    let teamsQuery = client.from("sales_teams").select("id").eq("is_active", true);
    if (data.teamId) {
      const { data: teamRow } = await client
        .from("sales_teams")
        .select("manager_id")
        .eq("id", data.teamId)
        .single();
      const allowed =
        memberTeamIds.includes(data.teamId) ||
        teamRow?.manager_id === data.userId;
      if (!allowed) return { data: null, error: "Forbidden: team not accessible" };
      teamsQuery = teamsQuery.eq("id", data.teamId);
    } else if (memberTeamIds.length > 0) {
      teamsQuery = teamsQuery.or(
        `manager_id.eq.${data.userId},id.in.(${memberTeamIds.join(",")})`
      );
    } else {
      teamsQuery = teamsQuery.eq("manager_id", data.userId);
    }
    const { data: teams } = await teamsQuery;
    const teamIds = teams?.map((t) => t.id) ?? [];
    if (teamIds.length === 0) {
      return {
        data: {
          total_teams: 0,
          total_members: 0,
          total_revenue: 0,
          total_commission_paid: 0,
          pending_transactions: 0,
          approved_transactions: 0,
          conversion_rate: 0,
          average_deal_size: 0,
          top_performers: [],
          monthly_trends: [],
        },
        error: null,
      };
    }

    const txTable: SalesPortalTable = "sales_transactions";
    if (!assertTable(txTable)) return { data: null, error: "Invalid table" };
    const { data: transactions, error: txError } = await client
      .from("sales_transactions")
      .select("amount, commission_amount, status, transaction_type")
      .in("team_id", teamIds);
    if (txError) return { data: null, error: txError.message };

    const list = transactions ?? [];
    const total_revenue = list.reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const total_commission_paid = list.reduce(
      (s, t) => s + (Number(t.commission_amount) || 0),
      0
    );
    const pending_transactions = list.filter(
      (t) => t.status === "pending"
    ).length;
    const approved_transactions = list.filter(
      (t) => t.status === "approved" || t.status === "paid"
    ).length;
    const completedDeals = list.filter(
      (t) => t.transaction_type === "signed_sealed_delivered"
    ).length;
    const conversion_rate =
      list.length > 0 ? (completedDeals / list.length) * 100 : 0;
    const average_deal_size =
      list.length > 0 ? total_revenue / list.length : 0;

    return {
      data: {
        total_teams: teamIds.length,
        total_members: 0,
        total_revenue,
        total_commission_paid,
        pending_transactions,
        approved_transactions,
        conversion_rate,
        average_deal_size,
        top_performers: [],
        monthly_trends: [],
      },
      error: null,
    };
  });

/** Get commission payout history for the authenticated user (Sales Portal). */
export const getCommissionHistory = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      userId: z.string().uuid(),
      accessToken: jwtSchema,
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError };
    const access = await ensureSalesAccess(client, data.userId, data.accessToken);
    if (access) return { data: null, error: access.error };

    const table: SalesPortalTable = "commission_payouts";
    if (!assertTable(table)) return { data: null, error: "Invalid table" };
    const { data: payouts, error } = await client
      .from("commission_payouts")
      .select(
        "id, user_id, period_start, period_end, total_amount, status, approved_by, approved_at, paid_by, payment_date, payment_method, payment_reference, created_at"
      )
      .eq("user_id", data.userId)
      .order("created_at", { ascending: false });
    if (error) return { data: null, error: error.message };
    return { data: payouts ?? [], error: null };
  });

const salesFiltersSchema = z.object({
  team_id: z.string().uuid().optional(),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
  status: z.enum(["pending", "approved", "paid", "cancelled", "disputed"]).optional(),
});

/** Get sales transactions with optional filters (team, date range, status). */
export const getSalesTransactions = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      userId: z.string().uuid(),
      accessToken: jwtSchema,
      filters: salesFiltersSchema.optional(),
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError };
    const access = await ensureSalesAccess(client, data.userId, data.accessToken);
    if (access) return { data: null, error: access.error };

    const memberTable: SalesPortalTable = "sales_team_members";
    if (!assertTable(memberTable)) return { data: null, error: "Invalid table" };
    const { data: memberRows } = await client
      .from("sales_team_members")
      .select("team_id")
      .eq("user_id", data.userId)
      .eq("is_active", true);
    const memberTeamIds = memberRows?.map((m) => m.team_id) ?? [];

    const teamsTable: SalesPortalTable = "sales_teams";
    if (!assertTable(teamsTable)) return { data: null, error: "Invalid table" };
    let teamsQuery = client.from("sales_teams").select("id").eq("is_active", true);
    if (memberTeamIds.length > 0) {
      teamsQuery = teamsQuery.or(
        `manager_id.eq.${data.userId},id.in.(${memberTeamIds.join(",")})`
      );
    } else {
      teamsQuery = teamsQuery.eq("manager_id", data.userId);
    }
    const { data: teams } = await teamsQuery;
    const allowedTeamIds = teams?.map((t) => t.id) ?? [];
    if (allowedTeamIds.length === 0) return { data: [], error: null };

    const txTable: SalesPortalTable = "sales_transactions";
    if (!assertTable(txTable)) return { data: null, error: "Invalid table" };
    let query = client
      .from("sales_transactions")
      .select(
        "id, opportunity_id, team_id, affiliate_link_id, client_id, project_id, transaction_type, amount, commission_amount, commission_rate, status, sales_rep_id, closer_id, description, notes, created_by, approved_by, approved_at, paid_at, created_at, updated_at"
      )
      .in("team_id", allowedTeamIds);

    const f = data.filters;
    if (f?.team_id && allowedTeamIds.includes(f.team_id)) {
      query = query.eq("team_id", f.team_id);
    }
    if (f?.date_from) query = query.gte("created_at", f.date_from);
    if (f?.date_to) query = query.lte("created_at", f.date_to);
    if (f?.status) query = query.eq("status", f.status);

    const { data: rows, error } = await query.order("created_at", {
      ascending: false,
    });
    if (error) return { data: null, error: error.message };
    return { data: rows ?? [], error: null };
  });

// ============================================================================
// PARTNER MANAGEMENT SERVER FUNCTIONS
// ============================================================================

const partnerFiltersSchema = z.object({
  partner_type: z.enum(['channel', 'referral', 'reseller', 'technology', 'strategic', 'affiliate']).optional(),
  tier: z.enum(['bronze', 'silver', 'gold', 'platinum']).optional(),
  status: z.enum(['pending', 'active', 'inactive', 'suspended']).optional(),
  territory_id: z.string().uuid().optional(),
  search: z.string().optional(),
});

/** Get all partners with optional filtering. */
export const getPartners = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      userId: z.string().uuid(),
      accessToken: jwtSchema,
      filters: partnerFiltersSchema.optional(),
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError };
    const access = await ensureSalesAccess(client, data.userId, data.accessToken);
    if (access) return { data: null, error: access.error };

    const table: SalesPortalTable = "partners";
    if (!assertTable(table)) return { data: null, error: "Invalid table" };

    // Get user's organization
    const { data: profile } = await client
      .from("profiles")
      .select("organization_id")
      .eq("id", data.userId)
      .single();

    const organizationId = profile?.organization_id;
    if (!organizationId) return { data: null, error: "User not in an organization" };

    let query = client
      .from("partners")
      .select("*")
      .eq("organization_id", organizationId);

    const f = data.filters;
    if (f?.partner_type) query = query.eq("partner_type", f.partner_type);
    if (f?.tier) query = query.eq("tier", f.tier);
    if (f?.status) query = query.eq("status", f.status);
    if (f?.search) {
      query = query.or(`name.ilike.%${f.search}%,description.ilike.%${f.search}%`);
    }

    const { data: partners, error } = await query.order("name");
    if (error) return { data: null, error: error.message };
    return { data: partners ?? [], error: null };
  });

/** Get single partner by ID with details. */
export const getPartner = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      userId: z.string().uuid(),
      accessToken: jwtSchema,
      partnerId: z.string().uuid(),
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError };
    const access = await ensureSalesAccess(client, data.userId, data.accessToken);
    if (access) return { data: null, error: access.error };

    const table: SalesPortalTable = "partners";
    if (!assertTable(table)) return { data: null, error: "Invalid table" };

    // Get user's organization
    const { data: profile } = await client
      .from("profiles")
      .select("organization_id")
      .eq("id", data.userId)
      .single();

    const organizationId = profile?.organization_id;
    if (!organizationId) return { data: null, error: "User not in an organization" };

    const { data: partner, error } = await client
      .from("partners")
      .select("*")
      .eq("id", data.partnerId)
      .eq("organization_id", organizationId)
      .single();

    if (error) return { data: null, error: error.message };
    return { data: partner, error: null };
  });

const createPartnerSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  partner_type: z.enum(['channel', 'referral', 'reseller', 'technology', 'strategic', 'affiliate']),
  tier: z.enum(['bronze', 'silver', 'gold', 'platinum']).default('bronze'),
  contact_name: z.string().optional(),
  contact_email: z.string().email().optional(),
  contact_phone: z.string().optional(),
  website: z.string().url().optional(),
  commission_rate: z.number().min(0).max(1).default(0.10),
  revenue_share_percentage: z.number().min(0).max(100).optional(),
  territory_ids: z.array(z.string().uuid()).optional(),
  geographic_coverage: z.array(z.string()).optional(),
  industry_focus: z.array(z.string()).optional(),
  contract_start_date: z.string().optional(),
  contract_end_date: z.string().optional(),
});

/** Create a new partner. */
export const createPartner = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      userId: z.string().uuid(),
      accessToken: jwtSchema,
      partner: createPartnerSchema,
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError };
    const access = await ensureSalesAccess(client, data.userId, data.accessToken);
    if (access) return { data: null, error: access.error };

    const table: SalesPortalTable = "partners";
    if (!assertTable(table)) return { data: null, error: "Invalid table" };

    // Get user's organization
    const { data: profile } = await client
      .from("profiles")
      .select("organization_id")
      .eq("id", data.userId)
      .single();

    const organizationId = profile?.organization_id;
    if (!organizationId) return { data: null, error: "User not in an organization" };

    const { data: partner, error } = await client
      .from("partners")
      .insert({
        ...data.partner,
        organization_id: organizationId,
        created_by: data.userId,
        status: 'pending',
        total_referrals: 0,
        total_revenue_generated: 0,
        total_commission_earned: 0,
        active_deals: 0,
      })
      .select()
      .single();

    if (error) return { data: null, error: error.message };

    const { publishMyceliumEvent } = await import("./_mycelium.server");
    await publishMyceliumEvent(client, {
      domain: "sales",
      type: "partner_created",
      userId: data.userId,
      payload: { partner_id: partner.id, organization_id: organizationId },
    });

    return { data: partner, error: null };
  });

const updatePartnerSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  partner_type: z.enum(['channel', 'referral', 'reseller', 'technology', 'strategic', 'affiliate']).optional(),
  tier: z.enum(['bronze', 'silver', 'gold', 'platinum']).optional(),
  status: z.enum(['pending', 'active', 'inactive', 'suspended']).optional(),
  contact_name: z.string().optional(),
  contact_email: z.string().email().optional(),
  contact_phone: z.string().optional(),
  website: z.string().url().optional(),
  commission_rate: z.number().min(0).max(1).optional(),
  revenue_share_percentage: z.number().min(0).max(100).optional(),
  territory_ids: z.array(z.string().uuid()).optional(),
  geographic_coverage: z.array(z.string()).optional(),
  industry_focus: z.array(z.string()).optional(),
  contract_start_date: z.string().optional(),
  contract_end_date: z.string().optional(),
});

/** Update an existing partner. */
export const updatePartner = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      userId: z.string().uuid(),
      accessToken: jwtSchema,
      partnerId: z.string().uuid(),
      updates: updatePartnerSchema,
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError };
    const access = await ensureSalesAccess(client, data.userId, data.accessToken);
    if (access) return { data: null, error: access.error };

    const table: SalesPortalTable = "partners";
    if (!assertTable(table)) return { data: null, error: "Invalid table" };

    // Get user's organization
    const { data: profile } = await client
      .from("profiles")
      .select("organization_id")
      .eq("id", data.userId)
      .single();

    const organizationId = profile?.organization_id;
    if (!organizationId) return { data: null, error: "User not in an organization" };

    const { data: partner, error } = await client
      .from("partners")
      .update(data.updates)
      .eq("id", data.partnerId)
      .eq("organization_id", organizationId)
      .select()
      .single();

    if (error) return { data: null, error: error.message };

    const { publishMyceliumEvent } = await import("./_mycelium.server");
    await publishMyceliumEvent(client, {
      domain: "sales",
      type: "partner_updated",
      userId: data.userId,
      payload: { partner_id: data.partnerId, organization_id: organizationId },
    });

    return { data: partner, error: null };
  });

/** Get partner performance metrics. */
export const getPartnerPerformance = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      userId: z.string().uuid(),
      accessToken: jwtSchema,
      partnerId: z.string().uuid(),
    })
  )
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError };
    const access = await ensureSalesAccess(client, data.userId, data.accessToken);
    if (access) return { data: null, error: access.error };

    const table: SalesPortalTable = "partners";
    if (!assertTable(table)) return { data: null, error: "Invalid table" };

    // Get user's organization
    const { data: profile } = await client
      .from("profiles")
      .select("organization_id")
      .eq("id", data.userId)
      .single();

    const organizationId = profile?.organization_id;
    if (!organizationId) return { data: null, error: "User not in an organization" };

    const { data: partner, error } = await client
      .from("partners")
      .select("*")
      .eq("id", data.partnerId)
      .eq("organization_id", organizationId)
      .single();

    if (error) return { data: null, error: error.message };
    if (!partner) return { data: null, error: "Partner not found" };

    // Calculate trend based on recent performance (simplified)
    const trend = partner.total_revenue_generated > 0 ? 'up' : 'stable';

    return {
      data: {
        partner_id: partner.id,
        partner_name: partner.name,
        partner_type: partner.partner_type,
        tier: partner.tier,
        total_referrals: partner.total_referrals,
        total_revenue: partner.total_revenue_generated,
        total_commission: partner.total_commission_earned,
        conversion_rate: partner.conversion_rate ?? 0,
        active_territories: partner.territory_ids?.length ?? 0,
        active_deals: partner.active_deals,
        trend,
      },
      error: null,
    };
  });
